import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'
import { Invoice, PaginationModel } from '$models'
import { invoiceService } from '$services'
import { exceptionMessageHandler, logger } from '$utils'
import { Dayjs } from 'dayjs'
import { isCancelledError } from '@tanstack/react-query'
import { InvoiceStatusFilter, InvoiceVersion } from '$constants'
import {
  extractPaginationModel,
  extractSortModel,
  parseSortModelToParamString,
} from '../../../common/helpers'
import { GridSortModel } from '@mui/x-data-grid-pro'

import {
  INITIAL_POLL_DELAY,
  INVOICE_POLL_INTERVAL,
  MAX_INVOICE_POLL_COUNT,
} from './_common/constants'

let listInvoicePollTimeoutId: NodeJS.Timeout | null = null
let listInvoicePollCount = 0

interface InvoicesState {
  invoices: Invoice[]
  paginationModel: PaginationModel
  sortModel: GridSortModel
  processingInvoiceId: string | null
}

export interface InvoiceQueryConditions {
  start?: Dayjs
  end?: Dayjs
  invoiceNumber?: string
  supplierName?: string
  status?: InvoiceStatusFilter
  exactMatch?: boolean
  page: number
  pageSize: number
  sort?: string
}

const defaultInvoiceQueryConditions = (
  queryCondition: Partial<InvoiceQueryConditions> = {}
) => {
  return {
    start: queryCondition.start,
    end: queryCondition.end,
    invoiceNumber: queryCondition.invoiceNumber,
    supplierName: queryCondition.supplierName,
    status: queryCondition.status,
    exactMatch: queryCondition.exactMatch,
    page: queryCondition.page ?? 0,
    pageSize: queryCondition.pageSize ?? 20,
    sort: queryCondition.sort,
  }
}

export interface InvoicesSlice extends InvoicesState {
  /**
   * Resets the Invoice Slice state
   */
  resetInvoicesSlice: () => void

  /**
   * Get invoices list
   */
  getInvoices(queryConditions?: InvoiceQueryConditions): Promise<void>

  /**
   * HARD delete an invoice file.
   * This should only be used on invoices that have not been worked on e.g. Failed, or Processing invoices
   */
  deleteInvoiceFile: (fileId?: string) => Promise<void>

  /**
   * Sets the invoices state
   * @param invoices The array of invoices to set the invoices state to
   */
  setInvoices: (invoices: Invoice[]) => void

  /**
   * Sets the pagination model for invoice and invoice history Grids
   * @param paginationModel the pagination model to set
   * @returns void
   */
  setPagination: (paginationModel: {
    page: number
    pageSize: number
    total?: number
  }) => void

  /**
   * Starts polling for an uploaded invoice until AI processing completes,
   * then refreshes the grid
   */
  startListInvoicePolling: (invoiceId: string) => void

  /**
   * Stops any active list-page invoice polling
   */
  stopListInvoicePolling: () => void
}

const initialState: InvoicesState = {
  invoices: [],
  paginationModel: {
    page: 0,
    pageSize: 20,
    total: -1, // -1 means the total is unknown
  },
  sortModel: [
    {
      field: 'issuedAt',
      sort: 'desc',
    },
  ],
  processingInvoiceId: null,
}

const createInvoicesSlice: StateCreator<
  InvoicesSlice & SharedSlice,
  [],
  [],
  InvoicesSlice
> = (set, get) => ({
  // State variables
  ...initialState,

  // State setters
  resetInvoicesSlice: () => {
    get().stopListInvoicePolling()
    set(initialState)
  },

  // State API calls
  getInvoices: async (queryConditions): Promise<void> => {
    try {
      const finalQueryConditions =
        defaultInvoiceQueryConditions(queryConditions)
      set(() => ({ isLoading: true }))
      const response = await invoiceService.getInvoices(finalQueryConditions)
      set(() => ({
        invoices: response.result,
        paginationModel: extractPaginationModel(response),
        sortModel: extractSortModel(response),
      }))
    } catch (error) {
      if (isCancelledError(error)) {
        logger.debug('Invoices query was cancelled')
        // We do a return here as we don't want to update the state if the query was cancelled,
        // otherwise it would be an empty array until the last call finishes and return the values
        return
      }

      set(() => ({
        invoices: [],
      }))
      exceptionMessageHandler(error, 'Failed to retrieve incoming invoices')
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  deleteInvoiceFile: async (fileId?: string): Promise<void> => {
    if (!fileId) return
    return invoiceService.deleteInvoiceFile(fileId).catch((error) => {
      exceptionMessageHandler(error, 'Failed to delete invoice file')
    })
  },

  setInvoices: (invoices: Invoice[]): void => {
    set(() => ({
      invoices: invoices,
    }))
  },

  setPagination: (paginationModel: {
    page: number
    pageSize: number
    total?: number
  }): void => {
    set(() => ({
      paginationModel: {
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        total: paginationModel.total ?? -1,
      },
    }))
  },

  stopListInvoicePolling: (): void => {
    if (listInvoicePollTimeoutId) {
      clearTimeout(listInvoicePollTimeoutId)
      listInvoicePollTimeoutId = null
    }
    listInvoicePollCount = 0
    set(() => ({ processingInvoiceId: null }))
  },

  startListInvoicePolling: (invoiceId: string): void => {
    get().stopListInvoicePolling()
    set(() => ({ processingInvoiceId: invoiceId }))

    const poll = async (): Promise<void> => {
      if (listInvoicePollCount >= MAX_INVOICE_POLL_COUNT) {
        get().stopListInvoicePolling()
        exceptionMessageHandler(
          new Error('Processing timeout'),
          'The invoice is taking too long to process. Please refresh the page.'
        )
        return
      }

      try {
        const invoice = await invoiceService.getInvoice(invoiceId)

        if (
          invoice?.version === InvoiceVersion.Current ||
          invoice?.version === InvoiceVersion.Failed
        ) {
          get().stopListInvoicePolling()
          const { paginationModel, sortModel } = get()
          await get().getInvoices({
            status: InvoiceStatusFilter.NotReceived,
            page: 0,
            pageSize: paginationModel.pageSize,
            sort: parseSortModelToParamString(sortModel),
          })
          return
        }

        listInvoicePollCount += 1
        listInvoicePollTimeoutId = setTimeout(poll, INVOICE_POLL_INTERVAL)
      } catch (error) {
        // 404 is expected while the invoice is being created — keep polling
        const is404 =
          error &&
          typeof error === 'object' &&
          'status' in error &&
          error.status === 404
        if (is404) {
          listInvoicePollCount += 1
          listInvoicePollTimeoutId = setTimeout(poll, INVOICE_POLL_INTERVAL)
          return
        }

        get().stopListInvoicePolling()
        exceptionMessageHandler(
          error,
          'Failed to check invoice processing status'
        )
      }
    }

    listInvoicePollTimeoutId = setTimeout(poll, INITIAL_POLL_DELAY)
  },
})

export default createInvoicesSlice
